import React, { useState, useEffect } from 'react';
import {
  Shield,
  Lock,
  Check,
  ArrowRight,
  Target,
  Brain,
  GitBranch,
  BarChart3,
  FileCheck,
  Globe,
  Zap,
  Users,
  Award,
  ChevronRight,
  Github,
  Download,
  AlertTriangle,
  TrendingUp,
  Cpu,
  Mail,
  MessageCircle,
  Play,
  X
} from 'lucide-react';

const LandingPage = ({ onLogin }) => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [showDemo, setShowDemo] = useState(false);
  const [demoStep, setDemoStep] = useState(0);

  // Auto-advance demo steps
  useEffect(() => {
    if (showDemo) {
      const timer = setInterval(() => {
        setDemoStep(prev => (prev + 1) % 5);
      }, 3000);
      return () => clearInterval(timer);
    }
  }, [showDemo]);

  const features = [
    {
      icon: <Brain size={32} />,
      title: "AI-Powered Analysis",
      description: "Leverage GPT-4o and Claude 4 Sonnet for intelligent threat identification and risk assessment using state-of-the-art AI models.",
      details: "Our AI engine analyzes your system architecture and automatically generates comprehensive threat scenarios with detailed mitigation strategies."
    },
    {
      icon: <Target size={32} />,
      title: "Multiple Frameworks",
      description: "Comprehensive coverage with OWASP, STRIDE, MITRE ATT&CK, MAESTRO, and LINDDUN frameworks for complete security analysis.",
      details: "Get industry-standard threat modeling with automatic framework selection based on your system type and requirements."
    },
    {
      icon: <BarChart3 size={32} />,
      title: "Risk Scoring & Analytics",
      description: "Advanced CVSS v3.1 and DREAD scoring with visual analytics to prioritize security investments and remediation efforts.",
      details: "Transform complex threat data into actionable insights with risk scoring, trend analysis, and executive dashboards."
    },
    {
      icon: <GitBranch size={32} />,
      title: "CI/CD Integration",
      description: "Seamlessly integrate threat modeling into your DevSecOps pipeline with API tokens and automated scanning capabilities.",
      details: "Generate API tokens for CI/CD pipelines, automate GitHub organization scanning, and get security feedback during development."
    }
  ];

  const frameworks = [
    {
      name: "OWASP Top 10",
      description: "Web application security risks",
      details: "Protects against injection attacks, broken authentication, sensitive data exposure, XML external entities, broken access control, security misconfigurations, XSS, insecure deserialization, vulnerable components, and insufficient logging."
    },
    {
      name: "OWASP LLM Top 10",
      description: "AI/ML security vulnerabilities",
      details: "Guards against prompt injection, insecure output handling, training data poisoning, model denial of service, supply chain vulnerabilities, sensitive information disclosure, insecure plugin design, excessive agency, overreliance, and model theft."
    },
    {
      name: "OWASP MCP Top 10",
      description: "Model Context Protocol security",
      details: "Secures AI integrations by addressing tool poisoning, excessive permissions, insecure credential storage, command injection, unsafe code execution, remote access exploits, data exfiltration, privilege escalation, prompt injection via tools, and lack of audit logging."
    },
    {
      name: "STRIDE",
      description: "Microsoft threat modeling methodology",
      details: "Systematically identifies threats across six categories: Spoofing identity, Tampering with data, Repudiation of actions, Information disclosure, Denial of service, and Elevation of privilege."
    },
    {
      name: "MITRE ATT&CK",
      description: "Adversarial tactics & techniques",
      details: "Maps threats to real-world attack patterns observed in the wild, covering initial access, execution, persistence, privilege escalation, defense evasion, credential access, discovery, lateral movement, collection, exfiltration, and impact."
    },
    {
      name: "MAESTRO",
      description: "7-layer AI security framework",
      details: "Comprehensive AI protection across foundation models, data pipelines, model training, deployment infrastructure, API security, monitoring systems, and governance controls to ensure end-to-end AI system security."
    },
    {
      name: "LINDDUN",
      description: "Privacy-focused threat modeling",
      details: "Protects user privacy by addressing Linkability, Identifiability, Non-repudiation, Detectability, Disclosure of information, Unawareness, and Non-compliance with privacy regulations like GDPR."
    },
    {
      name: "ISO/IEC 42001",
      description: "AI Management System standard",
      details: "International standard for AI governance providing requirements for establishing, implementing, maintaining, and continually improving an AI management system. Covers risk management, data quality, transparency, and responsible AI practices."
    },
    {
      name: "Golden Controls",
      description: "Enterprise AI security controls",
      details: "Curated set of 12 comprehensive controls across 6 risk categories: AI Governance, Data Risk, Model Risk, System Risk, Operational Risk, and Compliance Risk. Automated threat-to-control mapping for enterprise security posture."
    }
  ];

  const useCases = [
    {
      icon: <Globe size={24} />,
      title: "Web Applications",
      description: "Comprehensive security analysis for web apps, APIs, and cloud services with OWASP coverage.",
      examples: ["E-commerce platforms", "SaaS applications", "REST/GraphQL APIs"]
    },
    {
      icon: <Cpu size={24} />,
      title: "AI/ML Systems",
      description: "Specialized threat modeling for agentic AI systems, LLMs, and machine learning pipelines.",
      examples: ["AI chatbots", "ML prediction models", "Autonomous agents"]
    },
    {
      icon: <FileCheck size={24} />,
      title: "Enterprise Systems",
      description: "ISO 42001, SOC 2, and PCI-DSS compliance mapping for enterprise security requirements.",
      examples: ["Payment systems", "Healthcare platforms", "Financial services"]
    }
  ];

  const benefits = [
    { icon: <Zap size={20} />, text: "10x faster threat identification compared to manual analysis" },
    { icon: <Users size={20} />, text: "Multi-role perspectives (security, cloud, network, AI specialists)" },
    { icon: <Award size={20} />, text: "Industry-standard compliance and certification support" },
    { icon: <TrendingUp size={20} />, text: "Continuous monitoring and automated security scanning" }
  ];

  const pricingPlans = [
    {
      name: "Free Trial",
      price: "Free",
      description: "Perfect for evaluating the platform",
      features: [
        "1 Threat Model Scan",
        "1 GitHub Organization Scan",
        "All 9 Frameworks & Standards",
        "Basic Risk Scoring",
        "JSON Export"
      ],
      cta: "Start Free Trial",
      highlighted: false
    },
    {
      name: "Professional",
      price: "Contact Us",
      description: "For teams and enterprises",
      features: [
        "Unlimited Threat Model Scans",
        "Unlimited GitHub Org Scans",
        "All 9 Frameworks & Standards",
        "Advanced CVSS & DREAD Scoring",
        "Golden Controls Mapping (12 Controls)",
        "ISO 42001 Compliance Scanning",
        "All Export Formats (SARIF, SPDX, CycloneDX)",
        "API Access & CI/CD Integration",
        "Priority Support"
      ],
      cta: "Contact Sales",
      highlighted: true
    }
  ];

  const demoSteps = [
    {
      title: "1. Describe Your System",
      content: "Enter your system architecture description or upload documentation...",
      visual: (
        <div className="demo-input">
          <div className="demo-textarea">
            <span className="typing-text">A web application with React frontend, FastAPI backend, PostgreSQL database, and AI-powered threat analysis...</span>
          </div>
        </div>
      )
    },
    {
      title: "2. Select Frameworks",
      content: "Choose the security frameworks for your analysis...",
      visual: (
        <div className="demo-frameworks">
          <span className="demo-chip selected">OWASP Top 10</span>
          <span className="demo-chip selected">STRIDE</span>
          <span className="demo-chip selected">MITRE ATT&CK</span>
          <span className="demo-chip">MAESTRO</span>
        </div>
      )
    },
    {
      title: "3. AI Analysis Running",
      content: "Our AI analyzes your system for potential threats...",
      visual: (
        <div className="demo-analysis">
          <div className="analysis-progress">
            <div className="progress-bar" style={{ width: '75%' }}></div>
          </div>
          <span>Analyzing architecture... Identifying threats...</span>
        </div>
      )
    },
    {
      title: "4. Threats Identified",
      content: "Review comprehensive threat scenarios with risk scores...",
      visual: (
        <div className="demo-threats">
          <div className="demo-threat high">
            <AlertTriangle size={14} />
            <span>SQL Injection Risk</span>
            <span className="score">CVSS 8.5</span>
          </div>
          <div className="demo-threat medium">
            <AlertTriangle size={14} />
            <span>Authentication Bypass</span>
            <span className="score">CVSS 6.2</span>
          </div>
          <div className="demo-threat low">
            <AlertTriangle size={14} />
            <span>Information Disclosure</span>
            <span className="score">CVSS 3.1</span>
          </div>
        </div>
      )
    },
    {
      title: "5. Export & Integrate",
      content: "Download reports in multiple formats or integrate with CI/CD...",
      visual: (
        <div className="demo-export">
          <button className="demo-export-btn"><Download size={14} /> SARIF</button>
          <button className="demo-export-btn"><Download size={14} /> PDF Report</button>
          <button className="demo-export-btn"><Github size={14} /> CI/CD</button>
        </div>
      )
    }
  ];

  return (
    <div className="landing-page">
      {/* Header Navigation */}
      <header className="landing-header">
        <div className="landing-header-container">
          <div className="landing-logo">
            <Shield size={28} />
            <span>Agentic Threat Modeler</span>
          </div>
          <nav className="landing-nav">
            <button
              className="nav-link-btn"
              onClick={() => {
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Pricing
            </button>
            <button className="login-button" onClick={onLogin}>
              <Lock size={16} />
              <span>Login</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Demo Modal */}
      {showDemo && (
        <div className="demo-modal-overlay" onClick={() => setShowDemo(false)}>
          <div className="demo-modal" onClick={e => e.stopPropagation()}>
            <button className="demo-close" onClick={() => setShowDemo(false)}>
              <X size={24} />
            </button>
            <h2>How It Works</h2>
            <div className="demo-content">
              <div className="demo-steps-nav">
                {demoSteps.map((step, idx) => (
                  <div
                    key={idx}
                    className={`demo-step-dot ${idx === demoStep ? 'active' : ''} ${idx < demoStep ? 'completed' : ''}`}
                    onClick={() => setDemoStep(idx)}
                  />
                ))}
              </div>
              <div className="demo-step-content">
                <h3>{demoSteps[demoStep].title}</h3>
                <p>{demoSteps[demoStep].content}</p>
                <div className="demo-visual">
                  {demoSteps[demoStep].visual}
                </div>
              </div>
            </div>
            <div className="demo-cta">
              <button className="cta-primary" onClick={() => { setShowDemo(false); onLogin(); }}>
                <Lock size={20} />
                <span>Try It Now</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <Shield size={16} />
              <span>AI-Powered Security Analysis</span>
            </div>
            <h1 className="hero-title">
              Next-Generation<br />
              <span className="gradient-text">Threat Modeling</span>
            </h1>
            <p className="hero-description">
              Automate security threat analysis with AI-powered insights across 7 industry frameworks,
              ISO 42001 compliance scanning, and Golden Controls mapping. Get comprehensive threat models,
              risk scoring, and enterprise compliance in minutes, not weeks.
            </p>
            <div className="hero-actions">
              <button className="cta-primary" onClick={onLogin}>
                <Lock size={20} />
                <span>Start Free Analysis</span>
                <ArrowRight size={16} />
              </button>
              <button className="cta-secondary" onClick={() => setShowDemo(true)}>
                <Play size={20} />
                <span>Watch Demo</span>
              </button>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <div className="stat-number">9</div>
                <div className="stat-label">Frameworks & Standards</div>
              </div>
              <div className="stat">
                <div className="stat-number">12</div>
                <div className="stat-label">Golden Controls</div>
              </div>
              <div className="stat">
                <div className="stat-number">5</div>
                <div className="stat-label">Export Formats</div>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="threat-model-preview">
              <div className="preview-header">
                <div className="preview-title">
                  <Shield size={20} />
                  <span>Live Threat Analysis</span>
                </div>
                <div className="preview-status">
                  <div className="status-dot active"></div>
                  <span>Running</span>
                </div>
              </div>
              <div className="preview-content">
                <div className="framework-chips">
                  <span className="chip active">OWASP</span>
                  <span className="chip active">STRIDE</span>
                  <span className="chip">MAESTRO</span>
                </div>
                <div className="threat-items">
                  <div className="threat-item">
                    <AlertTriangle size={16} color="#e74c3c" />
                    <span>Injection vulnerability detected</span>
                    <span className="severity high">High</span>
                  </div>
                  <div className="threat-item">
                    <AlertTriangle size={16} color="#f39c12" />
                    <span>Authentication bypass risk</span>
                    <span className="severity medium">Medium</span>
                  </div>
                  <div className="threat-item">
                    <AlertTriangle size={16} color="#3498db" />
                    <span>Data exposure potential</span>
                    <span className="severity low">Low</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="landing-section-header">
            <h2>Powerful Security Analysis Features</h2>
            <p>Everything you need for comprehensive threat modeling and risk assessment</p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`feature-card ${activeFeature === index ? 'active' : ''}`}
                onMouseEnter={() => setActiveFeature(index)}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <div className="feature-details">
                  <p>{feature.details}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Frameworks Section */}
      <section className="frameworks-section">
        <div className="container">
          <div className="landing-section-header">
            <h2>Security Frameworks & Compliance Standards</h2>
            <p>Comprehensive coverage across threat modeling methodologies, AI governance standards, and enterprise controls</p>
          </div>

          <div className="frameworks-grid">
            {frameworks.map((framework, index) => (
              <div key={index} className="framework-card">
                <h4>{framework.name}</h4>
                <p className="framework-subtitle">{framework.description}</p>
                <p className="framework-details">{framework.details}</p>
                <Check size={16} className="framework-check" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" id="pricing">
        <div className="container">
          <div className="landing-section-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>Start free, upgrade when you need more</p>
          </div>

          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.highlighted ? 'highlighted' : ''}`}>
                {plan.highlighted && <div className="pricing-badge">Most Popular</div>}
                <h3>{plan.name}</h3>
                <div className="pricing-price">{plan.price}</div>
                <p className="pricing-description">{plan.description}</p>
                <ul className="pricing-features">
                  {plan.features.map((feature, idx) => (
                    <li key={idx}>
                      <Check size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.highlighted ? (
                  <a href="mailto:hello@agenticthreat.ai?subject=Agentic Threat Modeler - Professional Plan Inquiry" className="cta-primary pricing-cta">
                    <Mail size={18} />
                    <span>{plan.cta}</span>
                  </a>
                ) : (
                  <button className="cta-secondary pricing-cta" onClick={onLogin}>
                    <Lock size={18} />
                    <span>{plan.cta}</span>
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="pricing-contact">
            <p>Need a custom plan for your enterprise? <a href="mailto:hello@agenticthreat.ai">Contact our sales team</a></p>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases-section">
        <div className="container">
          <div className="landing-section-header">
            <h2>Built for Every System Type</h2>
            <p>Specialized analysis for different architectures and compliance requirements</p>
          </div>

          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <div className="use-case-icon">{useCase.icon}</div>
                <h3>{useCase.title}</h3>
                <p>{useCase.description}</p>
                <ul className="use-case-examples">
                  {useCase.examples.map((example, idx) => (
                    <li key={idx}>
                      <ChevronRight size={14} />
                      {example}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="benefits-content">
            <div className="benefits-text">
              <h2>Why Choose Our Platform?</h2>
              <p>Transform your security posture with AI-powered threat modeling that scales with your organization.</p>

              <div className="benefits-list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="benefit-item">
                    <div className="benefit-icon">{benefit.icon}</div>
                    <span>{benefit.text}</span>
                  </div>
                ))}
              </div>

              <button className="cta-primary" onClick={onLogin}>
                <Lock size={20} />
                <span>Get Started Today</span>
                <ArrowRight size={16} />
              </button>
            </div>

            <div className="benefits-visual">
              <div className="dashboard-preview">
                <div className="preview-header">
                  <h4>Security Dashboard</h4>
                  <div className="preview-actions">
                    <Github size={16} />
                    <Download size={16} />
                    <BarChart3 size={16} />
                  </div>
                </div>
                <div className="dashboard-metrics">
                  <div className="metric">
                    <span className="metric-value">47</span>
                    <span className="metric-label">Threats Identified</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">8.2</span>
                    <span className="metric-label">Avg Risk Score</span>
                  </div>
                  <div className="metric">
                    <span className="metric-value">94%</span>
                    <span className="metric-label">Compliance Score</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Secure Your Systems?</h2>
            <p>Start with a free analysis or contact us for enterprise solutions</p>
            <div className="cta-actions">
              <button className="cta-primary large" onClick={onLogin}>
                <Lock size={24} />
                <span>Start Free Analysis</span>
                <ArrowRight size={20} />
              </button>
              <a href="mailto:hello@agenticthreat.ai?subject=Agentic Threat Modeler Inquiry" className="cta-secondary large">
                <Mail size={24} />
                <span>Contact Sales</span>
              </a>
            </div>
            <p className="cta-note">Free tier includes 1 scan and 1 GitHub org scan • No credit card required</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <Shield size={32} />
              <span>Agentic Threat Modeler</span>
            </div>
            <div className="footer-frameworks">
              <span>Powered by:</span>
              <div className="framework-logos">
                <span>OWASP</span>
                <span>STRIDE</span>
                <span>MITRE ATT&CK</span>
                <span>MAESTRO</span>
                <span>LINDDUN</span>
                <span>ISO 42001</span>
                <span>Golden Controls</span>
              </div>
            </div>
            <div className="footer-contact">
              <div className="contact-item">
                <Mail size={16} />
                <span>Sales & Inquiries: <a href="mailto:hello@agenticthreat.ai">hello@agenticthreat.ai</a></span>
              </div>
              <div className="contact-item">
                <MessageCircle size={16} />
                <span>Support: <a href="mailto:support@agenticthreat.ai">support@agenticthreat.ai</a></span>
              </div>
            </div>
            <div className="footer-ai">
              <span>AI Models: OpenAI GPT-4o • Anthropic Claude 4 Sonnet</span>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 Agentic Threat Modeler. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
